import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import AOS from 'aos';
import {
  FaArrowRight, FaSearch, FaFilter, FaTimes,
  FaUserTie, FaGraduationCap, FaBuilding, FaHandshake,
  FaShoppingCart, FaBullhorn, FaCheckCircle, FaCode,
  FaDatabase, FaCloud, FaLayerGroup
} from 'react-icons/fa';
import portfolioData from '../data/portfolio.json';
import './Portfolio.css';

const ICON_MAP = {
  FaUserTie: <FaUserTie />,
  FaGraduationCap: <FaGraduationCap />,
  FaBuilding: <FaBuilding />,
  FaHandshake: <FaHandshake />,
  FaShoppingCart: <FaShoppingCart />,
  FaBullhorn: <FaBullhorn />,
};

const CATEGORIES = ['All', 'HRMS', 'LMS', 'ERP', 'CRM', 'Ecommerce', 'Marketing'];

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    AOS.init({ duration: 900, once: true, offset: 80 });
    window.scrollTo(0, 0);
  }, []);

  const filtered = useMemo(() => {
    return portfolioData.filter(p => {
      const matchCat = activeCategory === 'All' || p.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || p.title.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q) ||
        p.technologies.some(t => t.toLowerCase().includes(q));
      return matchCat && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  const handleSearch = e => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  return (
    <div className="portfolio-page">

      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero-overlay" />
        <Container>
          <div className="page-hero-content" data-aos="fade-up">
            <div className="breadcrumb-vk">
              <Link to="/">Home</Link> / <span>Portfolio</span>
            </div>
            <h1 className="page-hero-title">
              Our Enterprise<br />
              <span>Product Portfolio</span>
            </h1>
            <p className="page-hero-desc">
              Explore our suite of enterprise-grade software products — from HRMS and ERP to LMS, CRM, and E-Commerce platforms. Each solution is purpose-built to solve real business challenges at scale.
            </p>
          </div>
        </Container>
      </section>

      {/* Stats Bar */}
      <section className="portfolio-stats-bar">
        <Container>
          <div className="pf-stats-grid">
            {[
              { value: '6+', label: 'Live Products', icon: <FaLayerGroup /> },
              { value: '5+', label: 'Global Clients', icon: <FaHandshake /> },
              { value: '100+', label: 'Projects Delivered', icon: <FaCode /> },
              { value: '5+', label: 'Tech Stacks', icon: <FaDatabase /> },
            ].map((s, i) => (
              <div key={i} className="pf-stat-item" data-aos="fade-up" data-aos-delay={i * 80}>
                <div className="pf-stat-icon">{s.icon}</div>
                <div className="pf-stat-value">{s.value}</div>
                <div className="pf-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Filter & Search */}
      <section className="portfolio-filter-section section-py">
        <Container>
          <div className="pf-filter-bar" data-aos="fade-up">
            {/* Category Tabs */}
            <div className="pf-cat-tabs">
              <FaFilter className="filter-icon" />
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  className={`pf-cat-btn${activeCategory === cat ? ' active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                  {cat !== 'All' && (
                    <span className="pf-cat-count">
                      {portfolioData.filter(p => p.category === cat).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search */}
            <form className="pf-search-form" onSubmit={handleSearch}>
              <div className="pf-search-wrap">
                <FaSearch className="pf-search-icon" />
                <input
                  type="text"
                  placeholder="Search by title, category, or technology..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pf-search-input"
                />
                {searchInput && (
                  <button type="button" className="pf-clear-btn" onClick={clearSearch}>
                    <FaTimes />
                  </button>
                )}
                <button type="submit" className="pf-search-btn">Search</button>
              </div>
            </form>
          </div>

          {/* Results count */}
          <div className="pf-results-info" data-aos="fade-up">
            <span>
              Showing <strong>{filtered.length}</strong> of <strong>{portfolioData.length}</strong> products
              {activeCategory !== 'All' && <> in <strong>{activeCategory}</strong></>}
              {searchQuery && <> matching "<strong>{searchQuery}</strong>"</>}
            </span>
          </div>

          {/* Grid */}
          {filtered.length > 0 ? (
            <Row className="g-4 mt-1">
              {filtered.map((project, i) => (
                <Col lg={4} md={6} key={project.id} data-aos="fade-up" data-aos-delay={i * 80}>
                  <div className="pf-card">
                    <div className="pf-card-header" style={{ background: `linear-gradient(135deg, ${project.color}15, ${project.color}06)` }}>
                      <div className="pf-card-icon" style={{ background: `${project.color}18`, color: project.color }}>
                        {ICON_MAP[project.icon]}
                      </div>
                      <div className="pf-card-meta">
                        <span className="pf-badge" style={{ background: project.color }}>{project.badge}</span>
                        <span className="pf-status-live">● {project.status}</span>
                      </div>
                    </div>
                    <div className="pf-card-body">
                      <h3 className="pf-card-title">{project.title}</h3>
                      <p className="pf-card-tagline" style={{ color: project.color }}>{project.tagline}</p>
                      <p className="pf-card-desc">{project.description.substring(0, 130)}...</p>

                      {/* Top Features */}
                      <ul className="pf-feature-list">
                        {project.features.slice(0, 3).map((f, fi) => (
                          <li key={fi}><FaCheckCircle style={{ color: project.color }} /> {f}</li>
                        ))}
                        {project.features.length > 3 && (
                          <li className="pf-feature-more">+{project.features.length - 3} more features</li>
                        )}
                      </ul>

                      {/* Tech Pills */}
                      <div className="pf-tech-pills">
                        {project.technologies.slice(0, 4).map((t, ti) => (
                          <span key={ti} className="pf-tech-pill">{t}</span>
                        ))}
                        {project.technologies.length > 4 && (
                          <span className="pf-tech-pill pf-tech-more">+{project.technologies.length - 4}</span>
                        )}
                      </div>
                    </div>
                    <div className="pf-card-footer">
                      <Link to={`/portfolio/${project.id}`} className="pf-view-btn" style={{ '--pf-color': project.color }}>
                        View Details <FaArrowRight />
                      </Link>
                      <Link to="/contact" className="pf-demo-btn">Request Demo</Link>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="pf-empty" data-aos="fade-up">
              <FaSearch className="pf-empty-icon" />
              <h4>No products found</h4>
              <p>Try adjusting your search or selecting a different category.</p>
              <button className="btn-primary-vk" onClick={() => { setActiveCategory('All'); clearSearch(); }}>
                Clear Filters
              </button>
            </div>
          )}
        </Container>
      </section>

      {/* Tech Stack Section */}
      <section className="pf-tech-section section-py">
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Technologies We Use</span>
            <h2 className="section-title">Our <span>Tech Stack</span></h2>
            <p className="section-subtitle">Enterprise-grade technologies powering every product we build.</p>
          </div>
          <Row className="g-4 mt-2">
            {[
              { category: 'Frontend', icon: <FaCode />, color: '#1e40af', techs: ['Angular', 'React', 'HTML5', 'CSS3', 'Bootstrap 5', 'TypeScript'] },
              { category: 'Backend', icon: <FaLayerGroup />, color: '#7c3aed', techs: ['.NET Core', 'C#', 'Python', 'REST APIs', 'GraphQL', 'Microservices'] },
              { category: 'Database', icon: <FaDatabase />, color: '#0891b2', techs: ['SQL Server', 'PostgreSQL', 'Redis', 'MongoDB', 'ElasticSearch'] },
              { category: 'Cloud & DevOps', icon: <FaCloud />, color: '#059669', techs: ['AWS', 'Azure', 'Docker', 'Kubernetes', 'CI/CD', 'DevOps'] },
            ].map((stack, i) => (
              <Col md={6} lg={3} key={i} data-aos="fade-up" data-aos-delay={i * 80}>
                <div className="pf-tech-card">
                  <div className="pf-tech-card-icon" style={{ background: `${stack.color}12`, color: stack.color }}>
                    {stack.icon}
                  </div>
                  <h4 className="pf-tech-card-title">{stack.category}</h4>
                  <div className="pf-tech-card-pills">
                    {stack.techs.map((t, ti) => (
                      <span key={ti} className="pf-tech-tag" style={{ borderColor: `${stack.color}40`, color: stack.color, background: `${stack.color}08` }}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="pf-cta-section">
        <Container>
          <div className="pf-cta-inner" data-aos="zoom-in">
            <h2 className="pf-cta-title">Ready to Build Your <span>Custom Solution?</span></h2>
            <p className="pf-cta-desc">
              Tell us about your project and get a free consultation from our enterprise team.
            </p>
            <div className="pf-cta-actions">
              <Link to="/contact" className="btn-primary-vk">Request Demo <FaArrowRight /></Link>
              <Link to="/contact" className="btn-outline-vk-white">Contact Us</Link>
            </div>
          </div>
        </Container>
      </section>

    </div>
  );
};

export default Portfolio;
